// RoomImageGallery — main featured image + horizontal thumbnail strip
// used on the individual hotel room cards.
//
// SINGLE SOURCE OF TRUTH FOR ROOM PHOTOS:
//   Every image rendered here comes from the `room_galleries` MongoDB
//   collection (managed through `/admin/galleries`). The gallery is
//   explicitly opted-out of the site-wide AutoImageEditor via
//   `data-no-cms-edit="true"` on the root so a pencil-edit here can
//   never fork the data into the parallel `image_slots` store.
//
//   When a managed context (`hotelSlug`, `roomSlug`) is provided AND the
//   user is in image-edit mode, we surface an overlay CTA that deep-links
//   into `/admin/galleries?hotel=…&room=…` — the canonical editor.
//
// Behaviour:
// - Single main image inside a fixed 16:10 wrapper (no layout shift).
// - Thumbnail strip below when >1 image; clicking swaps the main image
//   with a 500 ms cross-fade. The active thumbnail is highlighted AND
//   auto-scrolled into view so it stays visually anchored to the main
//   image at all times.
// - `activeIdx` clamps when the images list shrinks or grows so the
//   selected thumb always corresponds to the visible main image.
// - Falls back gracefully with a single image → no strip.
// - Broken URLs are dropped silently via `onError`.

import React, { useMemo, useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Images as ImagesIcon, ExternalLink } from "lucide-react";
import { useEditMode } from "@/contexts/CmsContext";
import { useLanguage } from "@/contexts/LanguageContext";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const RoomImageGallery = ({
  images,
  alt = "",
  aspect = "aspect-[16/10]",
  testIdPrefix = "room-gallery",
  // NEW · when the caller knows which managed gallery these images belong
  // to (hotel detail page), pass its context here. Enables the deep-link
  // to /admin/galleries when the admin toggles image-edit mode.
  managedGallery, // { hotelSlug, roomSlug }
}) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [broken, setBroken] = useState(() => new Set());
  const stripRef = useRef(null);
  const thumbRefs = useRef({});
  const { imageEditMode } = useEditMode();
  const { lang } = useLanguage();

  const cleanImages = useMemo(
    () => (images || []).filter((u) => typeof u === "string" && u && !broken.has(u)),
    [images, broken]
  );

  // If the images list shrinks / re-orders under us, clamp the active
  // index so the highlighted thumbnail always matches the main image.
  useEffect(() => {
    setActiveIdx((cur) => {
      if (!cleanImages.length) return 0;
      if (cur >= cleanImages.length) return cleanImages.length - 1;
      if (cur < 0) return 0;
      return cur;
    });
  }, [cleanImages.length]);

  const markBroken = (url) =>
    setBroken((cur) => {
      if (cur.has(url)) return cur;
      const next = new Set(cur);
      next.add(url);
      return next;
    });

  const safeIdx = Math.min(Math.max(activeIdx, 0), Math.max(cleanImages.length - 1, 0));

  // Keep the active thumbnail visible inside the horizontal strip.
  // Falls back to `scrollIntoView` on browsers without smooth behaviour.
  useEffect(() => {
    const strip = stripRef.current;
    const thumb = thumbRefs.current[safeIdx];
    if (!strip || !thumb) return;
    const stripRect = strip.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    if (thumbRect.left < stripRect.left || thumbRect.right > stripRect.right) {
      const delta =
        thumbRect.left - stripRect.left -
        (stripRect.width / 2 - thumbRect.width / 2);
      strip.scrollBy({ left: delta, behavior: "smooth" });
    }
  }, [safeIdx]);

  if (cleanImages.length === 0) return null;

  const hasMultiple = cleanImages.length > 1;
  const goPrev = () =>
    setActiveIdx((cur) => (cur <= 0 ? cleanImages.length - 1 : cur - 1));
  const goNext = () =>
    setActiveIdx((cur) => (cur >= cleanImages.length - 1 ? 0 : cur + 1));

  const managedEditLink =
    managedGallery && managedGallery.hotelSlug && managedGallery.roomSlug
      ? `/admin/galleries?hotel=${encodeURIComponent(managedGallery.hotelSlug)}&room=${encodeURIComponent(managedGallery.roomSlug)}`
      : null;

  return (
    <div
      className="flex flex-col"
      data-testid={testIdPrefix}
      // Opt every descendant image out of the site-wide AutoImageEditor so
      // pencil edits can NEVER fork the data into the slot-storage system.
      data-no-cms-edit="true"
      data-cms-room-gallery={managedGallery ? `${managedGallery.hotelSlug}/${managedGallery.roomSlug}` : undefined}
    >
      {/* Main featured image */}
      <div
        className={`relative overflow-hidden ${aspect} bg-[#1A1513] group`}
        data-testid={`${testIdPrefix}-main`}
      >
        {cleanImages.map((src, idx) => (
          <img
            key={`${idx}-${src}`}
            src={src}
            alt={alt}
            loading={idx === 0 ? "eager" : "lazy"}
            decoding="async"
            onError={() => markBroken(src)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out ${
              idx === safeIdx ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}

        {/* Prev / Next overlay buttons */}
        {hasMultiple ? (
          <>
            <button
              type="button"
              onClick={goPrev}
              className="absolute top-1/2 left-3 sm:left-4 -translate-y-1/2 z-10 w-10 h-10 sm:w-11 sm:h-11 inline-flex items-center justify-center bg-[#1A1513]/55 hover:bg-[#1A1513]/85 text-[#FDFBF7] border border-[#FDFBF7]/30 hover:border-[#D4A373] rounded-full opacity-90 hover:opacity-100 transition-all duration-300 backdrop-blur-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
              aria-label={`Previous image${alt ? ` of ${alt}` : ""}`}
              data-testid={`${testIdPrefix}-prev`}
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={1.6} />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="absolute top-1/2 right-3 sm:right-4 -translate-y-1/2 z-10 w-10 h-10 sm:w-11 sm:h-11 inline-flex items-center justify-center bg-[#1A1513]/55 hover:bg-[#1A1513]/85 text-[#FDFBF7] border border-[#FDFBF7]/30 hover:border-[#D4A373] rounded-full opacity-90 hover:opacity-100 transition-all duration-300 backdrop-blur-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
              aria-label={`Next image${alt ? ` of ${alt}` : ""}`}
              data-testid={`${testIdPrefix}-next`}
            >
              <ChevronRight className="w-5 h-5" strokeWidth={1.6} />
            </button>
          </>
        ) : null}

        {/* Edit-mode overlay · deep-links to the canonical /admin/galleries editor. */}
        {imageEditMode && managedEditLink ? (
          <a
            href={managedEditLink}
            className="absolute top-3 left-3 z-20 inline-flex items-center gap-1.5 px-3 py-2 bg-[#D4A373] hover:bg-[#C16542] text-[#1A1513] hover:text-[#FDFBF7] text-[10px] tracking-[0.22em] uppercase font-medium border border-[#1A1513]/10 shadow-lg transition-colors"
            data-no-cms-edit="true"
            data-testid={`${testIdPrefix}-manage-link`}
            title="Manage this room gallery from the single source of truth"
          >
            <ImagesIcon className="w-3.5 h-3.5" strokeWidth={1.8} />
            {pick(
              {
                en: "Manage gallery",
                fr: "Gérer la galerie",
                es: "Gestionar galería",
              },
              lang
            )}
            <ExternalLink className="w-3 h-3 opacity-70" strokeWidth={1.8} />
          </a>
        ) : null}
      </div>

      {/* Thumbnail strip */}
      {cleanImages.length > 1 ? (
        <div
          ref={stripRef}
          className="bg-[#1A1513] px-3 py-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid={`${testIdPrefix}-thumbs`}
        >
          <div className="flex items-center gap-2 min-w-min">
            {cleanImages.map((src, idx) => {
              const active = idx === safeIdx;
              return (
                <button
                  type="button"
                  key={`thumb-${idx}-${src}`}
                  ref={(el) => {
                    thumbRefs.current[idx] = el;
                  }}
                  onClick={() => setActiveIdx(idx)}
                  className={`relative shrink-0 w-16 sm:w-20 aspect-[4/3] overflow-hidden transition-all duration-300 ${
                    active
                      ? "outline outline-2 outline-offset-2 outline-[#D4A373] opacity-100"
                      : "opacity-70 hover:opacity-100"
                  }`}
                  aria-label={`Show image ${idx + 1}${alt ? ` of ${alt}` : ""}`}
                  aria-current={active}
                  data-testid={`${testIdPrefix}-thumb-${idx}`}
                >
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={() => markBroken(src)}
                    className="absolute inset-0 w-full h-full object-cover"
                    aria-hidden="true"
                  />
                  {!active ? (
                    <span
                      className="absolute inset-0 bg-[#1A1513]/30 pointer-events-none"
                      aria-hidden="true"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RoomImageGallery;
